import { useMemo } from 'react'

import { useConversations } from '@/features/chat/api/useConversation'
import { can } from '@/lib/can'
import type { ChatSubjectKind, Conversation, CurrentUser } from '@/types/domain'

/** The capability that opens every conversation, not only your own team's. */
export const CHAT_READ_ALL = 'collab.chat.read.all'

/** One member of a record's team, as both the case and quote wires spell it.
 *
 *  Structurally the common part of `CaseTeamMember` and `QuoteTeamMember`, not
 *  either of them: the predicate below needs the id and nothing else, and
 *  naming one of the two here would make a subject-agnostic rule take a side.
 *  `name` and `role` are declared, optional and unread, so a caller spelling a
 *  member inline is not an excess-property error. */
interface TeamMemberish {
  userId: string
  owner?: boolean
  name?: string
  role?: string
}

/** Whether this reader may open this record's conversation.
 *
 *  A MIRROR of the server rule (collab.ChatService.canReadSubject), not a
 *  control: the server refuses regardless, and it refuses with a 404 that says
 *  nothing. This exists so the interface does not offer a panel whose every
 *  request would answer 404 — a chat badge that opens onto an error is worse
 *  than no badge, and on the busiest screen in the product it would be the first
 *  thing a colleague reported as broken.
 *
 *  The row scope is deliberately NOT mirrored: a reader looking at the record
 *  already passed it, or the page itself would not have loaded.
 *
 *  `ownerId` is load-bearing and both call sites must pass it. The owner is
 *  seeded onto the team at create, so it looks redundant — but Unassign refuses
 *  only the removal that would EMPTY a team, so an owner can be taken off their
 *  own record while a colleague remains. The server keeps serving them the
 *  conversation; passing '' here is how the panel silently disappears for them. */
export function canSeeRecordChat(
  user: CurrentUser | null | undefined,
  ownerId: string,
  team: TeamMemberish[],
): boolean {
  if (!user) return false
  if (user.isSuperuser) return true
  if (can(user, CHAT_READ_ALL)) return true
  if (ownerId && ownerId === user.id) return true
  return team.some((member) => member.userId === user.id)
}

/** The conversation row for one record, from the list the chat page already
 *  loads.
 *
 *  The row carries the two facts a record panel needs and the record itself does
 *  not: `id`, which the socket subscribes to, and `unread`, which the header
 *  badge counts. A record nobody has spoken on has no row at all, and that is
 *  the ordinary state — the panel still renders, it simply has nothing to
 *  subscribe to until the first post invalidates this list. */
export function useRecordConversation(
  kind: ChatSubjectKind,
  subjectId: string,
  enabled = true,
): Conversation | undefined {
  const conversations = useConversations(enabled)
  return useMemo(
    () =>
      (conversations.data ?? []).find(
        (row) => row.subjectKind === kind && row.subjectId === subjectId,
      ),
    [conversations.data, kind, subjectId],
  )
}
